import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    BookOpen,
    Crown,
    Heart,
    ArrowLeftRight,
    ChevronDown,
    ChevronRight,
    DollarSign,
    FileText,
    TrendingUp,
    Target,
    Shield,
    Skull,
    Users,
    Scroll,
    Dices,
    CircleDot,
    ListChecks,
} from 'lucide-react';

type RoleTab = 'dom' | 'sub' | 'switch';

const roleTabs: { value: RoleTab; label: string; icon: typeof Crown }[] = [
    { value: 'dom', label: 'Dominant', icon: Crown },
    { value: 'sub', label: 'Submissive', icon: Heart },
    { value: 'switch', label: 'Switch', icon: ArrowLeftRight },
];

function Section({ title, icon: Icon, children }: { title: string; icon: typeof BookOpen; children: React.ReactNode }) {
    const [open, setOpen] = useState(true);
    return (
        <Card>
            <button
                onClick={() => setOpen(!open)}
                className="flex w-full items-center gap-3 px-5 py-4 text-left"
            >
                <Icon className="h-5 w-5 text-violet-400 shrink-0" />
                <span className="text-base font-semibold text-slate-200 flex-1">{title}</span>
                {open ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
            </button>
            {open && (
                <CardContent className="px-5 pb-5 pt-0 text-sm text-slate-400 space-y-3">
                    {children}
                </CardContent>
            )}
        </Card>
    );
}

function DomDocs() {
    return (
        <div className="space-y-4">
            <Section title="Getting Started" icon={BookOpen}>
                <p>As a Dominant, you create and manage debt contracts for your submissives. Here's how to get started:</p>
                <ol className="list-decimal list-inside space-y-1.5 text-slate-300">
                    <li>Go to <strong>My Subs</strong> to see submissives linked to you</li>
                    <li>Create a <strong>bespoke contract</strong> directly for a specific sub, or create a <strong>template</strong> and publish it to the marketplace</li>
                    <li>Once a sub signs the contract, it becomes active and interest begins accruing</li>
                </ol>
            </Section>

            <Section title="Contract Templates" icon={Scroll}>
                <p>Templates are reusable contract blueprints that can be published to the marketplace for any sub to sign.</p>
                <ul className="list-disc list-inside space-y-1.5 text-slate-300">
                    <li><strong>Create a template</strong> from the Templates page with all your desired terms</li>
                    <li>Set the opening balance, interest rate, compounding frequency, and minimum payment</li>
                    <li>Add custom fee types and admin fees</li>
                    <li>Publish to the marketplace or keep private</li>
                    <li>Multiple subs can sign the same template, each getting their own contract</li>
                </ul>
            </Section>

            <Section title="Bespoke Contracts" icon={FileText}>
                <p>Bespoke contracts are custom one-off contracts created directly for a specific submissive.</p>
                <ul className="list-disc list-inside space-y-1.5 text-slate-300">
                    <li>Navigate to <strong>My Subs</strong> and select the sub</li>
                    <li>Click <strong>New Contract</strong> to create a bespoke contract</li>
                    <li>The contract starts in <strong>Pending</strong> status until the sub signs</li>
                    <li>You can preconfigure wheel spins, dice games, and tasks while pending</li>
                    <li>Once signed, the start date and first payment due date are set automatically</li>
                </ul>
            </Section>

            <Section title="Interest & Compounding" icon={TrendingUp}>
                <p>Interest accrues automatically based on the contract's compounding settings.</p>
                <ul className="list-disc list-inside space-y-1.5 text-slate-300">
                    <li><strong>Annual rate</strong> is the stated interest rate (e.g., 25%)</li>
                    <li><strong>Compounding frequency</strong> determines how often interest is calculated: daily, weekly, biweekly, or monthly</li>
                    <li>Interest is applied to the current balance at each compounding period</li>
                    <li>The <strong>debt schedule</strong> shows a full amortization projection if the sub only pays the minimum</li>
                </ul>
            </Section>

            <Section title="Fees & Taxes" icon={DollarSign}>
                <p>You can define custom fees and taxes that are added to a sub's balance.</p>
                <ul className="list-disc list-inside space-y-1.5 text-slate-300">
                    <li><strong>Fee types</strong> can be triggered by: missed payments, minimum-only payments, or manual assessment</li>
                    <li><strong>Admin fees</strong> are recurring charges (e.g., weekly account maintenance fee)</li>
                    <li><strong>Manual fees/taxes</strong> can be assessed at any time from the contract detail page</li>
                    <li>All fees and taxes are recorded permanently in the immutable ledger</li>
                </ul>
            </Section>

            <Section title="Games & Activities" icon={Dices}>
                <p>Contracts can include interactive games that affect the sub's balance.</p>
                <ul className="list-disc list-inside space-y-1.5 text-slate-300">
                    <li><strong>Wheel of Fortune</strong> - Configure wheel segments with different fee/reward amounts. The sub spins and the result is applied to their balance</li>
                    <li><strong>Dice Games</strong> - Set up dice-based games with outcomes tied to balance changes</li>
                    <li><strong>Tasks</strong> - Assign tasks that the sub must complete. Tasks can carry fee penalties if not completed</li>
                    <li>Games can be configured on pending contracts before the sub signs</li>
                </ul>
            </Section>

            <Section title="Pillory & Wall of Shame" icon={Skull}>
                <p>Public punishment features for added humiliation.</p>
                <ul className="list-disc list-inside space-y-1.5 text-slate-300">
                    <li><strong>Pillory</strong> - Post a sub to the pillory for community voting. Votes can add fees to their contract balance</li>
                    <li><strong>Wall of Shame</strong> - A public display of subs and their debt levels</li>
                    <li>Both features are visible to all users on the platform</li>
                </ul>
            </Section>

            <Section title="Contract Management" icon={Shield}>
                <p>Managing active contracts as a Dom.</p>
                <ul className="list-disc list-inside space-y-1.5 text-slate-300">
                    <li><strong>View all contracts</strong> from the Contracts page or via My Subs</li>
                    <li><strong>Assess fees/taxes</strong> manually from the contract detail page</li>
                    <li><strong>Restricted changes</strong> (modifying interest rate, minimum payment, etc.) require double-consent from both parties</li>
                    <li><strong>Close a contract</strong> when the debt is paid off or by mutual agreement</li>
                    <li>The <strong>ledger</strong> on each contract shows every transaction permanently</li>
                </ul>
            </Section>
        </div>
    );
}

function SubDocs() {
    return (
        <div className="space-y-4">
            <Section title="Getting Started" icon={BookOpen}>
                <p>As a Submissive, you sign debt contracts created by Dominants and make payments on your debt. Here's how to get started:</p>
                <ol className="list-decimal list-inside space-y-1.5 text-slate-300">
                    <li>Browse the <strong>Marketplace</strong> to find contract templates from Doms</li>
                    <li>Review the contract terms carefully before signing</li>
                    <li>Once signed, the contract becomes active and you'll need to make payments</li>
                </ol>
            </Section>

            <Section title="Finding Contracts" icon={Scroll}>
                <p>There are two ways you may get a contract:</p>
                <ul className="list-disc list-inside space-y-1.5 text-slate-300">
                    <li><strong>Marketplace</strong> - Browse published contract templates and sign one that appeals to you</li>
                    <li><strong>Bespoke contracts</strong> - A Dom creates a custom contract specifically for you. You'll see it in your contracts list as "Pending" until you sign it</li>
                </ul>
            </Section>

            <Section title="Understanding Your Contract" icon={FileText}>
                <p>Each contract has key terms you should understand:</p>
                <ul className="list-disc list-inside space-y-1.5 text-slate-300">
                    <li><strong>Opening balance</strong> - The starting debt amount</li>
                    <li><strong>Interest rate</strong> - Annual percentage that compounds on your balance</li>
                    <li><strong>Compounding frequency</strong> - How often interest is added (daily, weekly, biweekly, monthly)</li>
                    <li><strong>Minimum payment</strong> - The least you must pay each period</li>
                    <li><strong>Admin fees</strong> - Recurring charges added to your balance each period</li>
                    <li><strong>Fee types</strong> - Additional fees that may be triggered by specific events</li>
                </ul>
            </Section>

            <Section title="Making Payments" icon={DollarSign}>
                <p>Payments reduce your contract balance.</p>
                <ul className="list-disc list-inside space-y-1.5 text-slate-300">
                    <li>Navigate to your contract detail page to make a payment</li>
                    <li>You can pay the minimum amount or more</li>
                    <li>Paying more than the minimum reduces your principal faster and saves on interest</li>
                    <li><strong>Warning:</strong> Paying only the minimum may trigger penalty fees if configured by your Dom</li>
                    <li>All payments are recorded permanently in the ledger</li>
                </ul>
            </Section>

            <Section title="Debt Schedule" icon={TrendingUp}>
                <p>The debt schedule shows a projection of your repayment timeline.</p>
                <ul className="list-disc list-inside space-y-1.5 text-slate-300">
                    <li>View the schedule from your contract detail page</li>
                    <li>Shows each payment period with balance, payment, interest, principal, and fees breakdown</li>
                    <li><strong>Total Paid</strong> column shows the cumulative amount you'll pay if making minimum payments</li>
                    <li>The schedule updates automatically when your balance changes</li>
                    <li>Paying more than the minimum will shorten the schedule</li>
                </ul>
            </Section>

            <Section title="Games & Activities" icon={Dices}>
                <p>Your Dom may configure interactive games on your contract.</p>
                <ul className="list-disc list-inside space-y-1.5 text-slate-300">
                    <li><strong>Wheel of Fortune</strong> - Spin the wheel and accept whatever outcome lands. Results affect your balance</li>
                    <li><strong>Dice Games</strong> - Roll dice with balance consequences</li>
                    <li><strong>Tasks</strong> - Complete assigned tasks. Failure to complete may result in additional fees</li>
                </ul>
            </Section>

            <Section title="Pillory & Public Features" icon={Target}>
                <p>Community features that may affect your experience.</p>
                <ul className="list-disc list-inside space-y-1.5 text-slate-300">
                    <li><strong>Pillory</strong> - Your Dom may post you to the pillory. Community members can vote to add fees to your balance</li>
                    <li><strong>Wall of Shame</strong> - Displays subs and their debt levels publicly</li>
                    <li><strong>Fee/Tax Feed</strong> - A live feed showing recent fees and taxes across the platform</li>
                </ul>
            </Section>

            <Section title="Your Rights" icon={Shield}>
                <p>Important protections for submissives:</p>
                <ul className="list-disc list-inside space-y-1.5 text-slate-300">
                    <li><strong>Consent required</strong> - You must sign a contract before it becomes active</li>
                    <li><strong>Double-consent for changes</strong> - Any restricted changes to contract terms require your approval</li>
                    <li><strong>Transparent ledger</strong> - Every charge is recorded and visible to you</li>
                    <li><strong>Reviews</strong> - After a contract ends, you can leave a review and rating for your Dom</li>
                </ul>
            </Section>
        </div>
    );
}

function SwitchDocs() {
    return (
        <div className="space-y-4">
            <Section title="Getting Started" icon={BookOpen}>
                <p>As a Switch, you can act as both a Dominant and a Submissive on the platform. You have access to all features of both roles.</p>
                <ul className="list-disc list-inside space-y-1.5 text-slate-300">
                    <li>Create and manage contracts as a Dom</li>
                    <li>Sign and pay on contracts as a Sub</li>
                    <li>Your navigation shows Dom-oriented features (My Subs, Templates) alongside everything else</li>
                </ul>
            </Section>

            <Section title="Acting as a Dominant" icon={Crown}>
                <p>When creating contracts, you have full Dom capabilities:</p>
                <ul className="list-disc list-inside space-y-1.5 text-slate-300">
                    <li>Create contract templates and publish to the marketplace</li>
                    <li>Create bespoke contracts for specific subs</li>
                    <li>Manage your subs from the <strong>My Subs</strong> page</li>
                    <li>Assess manual fees and taxes on active contracts</li>
                    <li>Configure wheel spins, dice games, and tasks</li>
                    <li>Post subs to the pillory</li>
                </ul>
                <p className="text-slate-500 text-xs">See the Dominant guide above for detailed documentation on each feature.</p>
            </Section>

            <Section title="Acting as a Submissive" icon={Heart}>
                <p>When signing contracts, you experience the Sub side:</p>
                <ul className="list-disc list-inside space-y-1.5 text-slate-300">
                    <li>Browse the marketplace for contracts to sign</li>
                    <li>Accept bespoke contracts from Doms</li>
                    <li>Make payments on your active debts</li>
                    <li>View your debt schedule and amortization projections</li>
                    <li>Participate in games configured by your Dom</li>
                </ul>
                <p className="text-slate-500 text-xs">See the Submissive guide above for detailed documentation on each feature.</p>
            </Section>

            <Section title="Tips for Switches" icon={ArrowLeftRight}>
                <p>Making the most of both roles:</p>
                <ul className="list-disc list-inside space-y-1.5 text-slate-300">
                    <li>You can simultaneously have contracts where you're the Dom and contracts where you're the Sub</li>
                    <li>Your <strong>Dashboard</strong> shows all contracts regardless of which side you're on</li>
                    <li>Use the <strong>Discover</strong> page to find other members — both potential subs and potential Doms</li>
                    <li>Your profile shows your Switch role, letting others know you play both sides</li>
                </ul>
            </Section>

            <Section title="Community Features" icon={Users}>
                <p>All community features are available to you:</p>
                <ul className="list-disc list-inside space-y-1.5 text-slate-300">
                    <li><strong>Wall of Shame</strong> - View (and appear on) the public debt display</li>
                    <li><strong>Pillory</strong> - Post others or be posted for community voting</li>
                    <li><strong>Fee/Tax Feed</strong> - Live feed of all platform activity</li>
                    <li><strong>Reviews</strong> - Leave and receive reviews based on your contracts</li>
                    <li><strong>Discover</strong> - Find and connect with other members</li>
                </ul>
            </Section>
        </div>
    );
}

export default function DocsPage() {
    const { user } = useAuth();

    const defaultTab: RoleTab = user?.role === 'sub' ? 'sub' : user?.role === 'switch' ? 'switch' : 'dom';
    const [activeTab, setActiveTab] = useState<RoleTab>(defaultTab);

    return (
        <div className="space-y-6 pb-20">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-xl font-semibold flex items-center gap-2">
                        <BookOpen className="h-5 w-5 text-violet-400" />
                        Documentation
                    </h1>
                    <p className="text-sm text-slate-400">Learn how to use DebtBound</p>
                </div>
                {user && (
                    <Link to="/dashboard">
                        <Button variant="outline" size="sm">Back to Dashboard</Button>
                    </Link>
                )}
            </div>

            {/* Role tabs */}
            <div className="flex gap-1 rounded-lg bg-slate-900 p-1">
                {roleTabs.map((tab) => (
                    <button
                        key={tab.value}
                        onClick={() => setActiveTab(tab.value)}
                        className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                            activeTab === tab.value
                                ? 'bg-violet-900/50 text-violet-300'
                                : 'text-slate-500 hover:text-slate-300'
                        }`}
                    >
                        <tab.icon className="h-4 w-4" />
                        {tab.label}
                    </button>
                ))}
            </div>

            {activeTab === 'dom' && <DomDocs />}
            {activeTab === 'sub' && <SubDocs />}
            {activeTab === 'switch' && <SwitchDocs />}
        </div>
    );
}
